import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import L from "../../../constants/baysportLabels";
import { calendarEntryTitle } from "../../../constants/calendarSessionTypes";
import ClientSearchPicker from "../common/ClientSearchPicker";
import { DesktopDialog, HubSheetActions } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import { Field, Input, Select } from "../../ui";
import SessionDateTimeFields from "./SessionDateTimeFields";
import CompetitionDetailsFields from "./CompetitionDetailsFields";
import CompetitionAudienceFields from "./CompetitionAudienceFields";

function CreateFormBody({
  kind,
  form,
  onChange,
  groups,
  branches,
  doctors,
  directions,
  coverPreviewUrl,
  onCoverFileSelect,
  onSubmit,
  isCompetition,
}) {
  return (
    <>
      {isCompetition ? (
        <div className={aa.calCreateHero}>
          <div className={aa.calCreateHeroGlow} aria-hidden />
          <span className={aa.calCreateHeroTag}>Соревнование</span>
          <p className={aa.calCreateHeroText}>
            Клиенты увидят событие в календаре и смогут подать заявку
          </p>
        </div>
      ) : null}

      <form
        id="cal-create-sheet-form"
        className={aa.calCreateForm}
        onSubmit={onSubmit}
      >
        {kind === "event" ? (
          <div className={aa.calCreateSection}>
            <Field label="Название">
              <Input
                value={form.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                placeholder="Название соревнования"
              />
            </Field>
          </div>
        ) : null}

        {isCompetition ? (
          <>
            <CompetitionAudienceFields
              form={form}
              onChange={onChange}
              groups={groups}
              directions={directions}
            />
            <CompetitionDetailsFields
              form={form}
              onChange={onChange}
              coverPreviewUrl={coverPreviewUrl}
              onCoverFileSelect={onCoverFileSelect}
              sheet
            />
          </>
        ) : null}

        {kind === "individual" ? (
          <div className={aa.calCreateSection}>
            <Field label="Клиент">
              <ClientSearchPicker
                value={form.patient_id}
                onChange={(patient_id) => onChange({ patient_id })}
                placeholder="ФИО или телефон"
              />
            </Field>
          </div>
        ) : null}

        {kind === "training" ? (
          <div className={aa.calCreateSection}>
            <Field label={L.group}>
              <Select
                value={form.specialization_id}
                onChange={(e) =>
                  onChange({ specialization_id: e.target.value })
                }
                required
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        ) : null}

        <div className={aa.calCreateSection}>
          <p className={aa.calCreateSectionTitle}>Время и ответственный</p>
          <SessionDateTimeFields
            sheet
            date={form.sessionDate}
            startTime={form.startTime}
            endTime={form.endTime}
            onChange={(patch) => onChange(patch)}
          />
          <Field label={L.trainer}>
            <Select
              value={form.doctor_id}
              onChange={(e) => onChange({ doctor_id: e.target.value })}
              required
            >
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.user?.full_name || d.full_name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {kind !== "event" ? (
          <div className={aa.calCreateSection}>
            <Field label="Зал">
              <Select
                value={form.branch_id}
                onChange={(e) => onChange({ branch_id: e.target.value })}
              >
                <option value="">Не указан</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        ) : null}

        <div className={aa.calCreateSection}>
          <Field label="Заметка для администратора">
            <Input
              value={form.notes}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="Необязательно"
            />
          </Field>
        </div>
      </form>
    </>
  );
}

export default function CalendarCreateSheet({
  open,
  kind,
  form,
  onChange,
  groups,
  branches,
  doctors,
  directions = [],
  saving,
  coverPreviewUrl,
  onCoverFileSelect,
  onClose,
  onSubmit,
}) {
  const isMobile = useIsMobile(768);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  if (!open || !kind) return null;

  const title = calendarEntryTitle(kind);
  const isCompetition = kind === "event";

  const body = (
    <CreateFormBody
      kind={kind}
      form={form}
      onChange={onChange}
      groups={groups}
      branches={branches}
      doctors={doctors}
      directions={directions}
      coverPreviewUrl={coverPreviewUrl}
      onCoverFileSelect={onCoverFileSelect}
      onSubmit={onSubmit}
      isCompetition={isCompetition}
    />
  );

  if (!isMobile) {
    return (
      <DesktopDialog
        open={open}
        onClose={onClose}
        title={title}
        wide
        dialogClassName={
          isCompetition
            ? `${aa.calCreateDialog} ${aa.calCreateDialogCompetition}`
            : aa.calCreateDialog
        }
        bodyClassName={aa.calCreateDialogBody}
        footer={
          <HubSheetActions
            primary={saving ? "Сохранение…" : "Создать"}
            secondaryLabel="Отмена"
            onSecondary={onClose}
            primaryForm="cal-create-sheet-form"
            primaryDisabled={saving}
          />
        }
      >
        {body}
      </DesktopDialog>
    );
  }

  return createPortal(
    <div className={aa.calSheetBackdrop} onClick={onClose} role="presentation">
      <div
        className={`${aa.calCreateSheet} ${isCompetition ? aa.calCreateSheetCompetition : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={aa.calSheetHandle} aria-hidden />
        <header className={aa.calCreateHead}>
          <h2 className={aa.calCreateTitle}>{title}</h2>
          <button
            type="button"
            className={aa.calSheetClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 6l12 12M18 6 6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {body}

        <footer className={aa.calCreateFoot}>
          <button type="button" className={aa.calCreateCancel} onClick={onClose}>
            Отмена
          </button>
          <button
            type="submit"
            form="cal-create-sheet-form"
            className={`${aa.calFilterApply} ${isCompetition ? aa.calCreateSubmitComp : ""}`}
            disabled={saving}
          >
            {saving ? "Сохранение…" : "Создать"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
